/**
 * Booking Creation Form Dialog
 * Opens when clicking calendar cells or "Create Booking" button
 */
import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { Alert, AlertDescription } from '../ui/alert';
import { Calendar, User, Mail, Phone, Users, Bed, Loader2, DollarSign } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';


export const BookingCreationDialog = ({
  open,
  onOpenChange,
  hotelId,
  prefilledDate = null,
  prefilledRoomType = null,
  onBookingCreated
}) => {
  const [loading, setLoading] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  
  const [formData, setFormData] = useState({
    guestName: '',
    guestEmail: '',
    guestPhone: '',
    checkIn: prefilledDate || '',
    checkOut: '',
    numGuests: 1,
    numRooms: 1,
    selectedRoomId: '',
    paymentMethod: 'cash',
    specialRequests: ''
  });

  useEffect(() => {
    if (open) {
      loadAvailableRooms();
      // Set prefilled data
      setFormData(prev => ({
        ...prev,
        checkIn: prefilledDate || prev.checkIn,
        selectedRoomId: prefilledRoomType || prev.selectedRoomId
      }));
    }
  }, [open, prefilledDate, prefilledRoomType]);

  const loadAvailableRooms = async () => {
    try {
      setLoadingRooms(true);
      const response = await fetch(`${BACKEND_URL}/api/pms/${hotelId}/available-rooms`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      // API returns { success: true, rooms: [...] }, extract the rooms array
      setAvailableRooms(data.rooms || []);
    } catch (error) {
      console.error('Error loading rooms:', error);
      toast.error('Failed to load available rooms');
    } finally {
      setLoadingRooms(false);
    }
  };

  const calculateNights = () => {
    if (formData.checkIn && formData.checkOut) {
      const checkIn = new Date(formData.checkIn);
      const checkOut = new Date(formData.checkOut);
      const nights = Math.ceil((checkOut - checkIn) / (1000 * 60 * 60 * 24));
      return nights > 0 ? nights : 0;
    }
    return 0;
  };

  const calculateEstimatedPrice = () => {
    const selectedRoom = availableRooms.find(r => r.id === formData.selectedRoomId);
    if (selectedRoom && formData.checkIn && formData.checkOut) {
      const nights = calculateNights();
      const basePrice = selectedRoom.price_per_night || 0;
      const totalRooms = parseInt(formData.numRooms) || 1;
      return (basePrice * nights * totalRooms).toFixed(2);
    }
    return '0.00';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.guestName.trim()) {
      toast.error('Please enter guest name');
      return;
    }
    if (!formData.guestEmail.trim()) {
      toast.error('Please enter guest email');
      return;
    }
    if (!formData.checkIn || !formData.checkOut) {
      toast.error('Please select check-in and check-out dates');
      return;
    }
    if (!formData.selectedRoomId) {
      toast.error('Please select a room');
      return;
    }
    
    const nights = calculateNights();
    if (nights <= 0) {
      toast.error('Check-out date must be after check-in date');
      return;
    }

    try {
      setLoading(true);
      
      // Create booking(s) - handle multiple rooms
      const numRooms = parseInt(formData.numRooms) || 1;
      const bookings = [];
      
      for (let i = 0; i < numRooms; i++) {
        bookings.push({
          guest_name: formData.guestName,
          guest_email: formData.guestEmail,
          guest_phone: formData.guestPhone || '',
          check_in: formData.checkIn,
          check_out: formData.checkOut,
          num_guests: parseInt(formData.numGuests),
          room_id: formData.selectedRoomId,
          payment_guarantee: formData.paymentMethod,
          special_requests: formData.specialRequests
        });
      }

      const response = await fetch(`${BACKEND_URL}/api/pms/booking/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          bookings: bookings
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`✅ ${numRooms} booking(s) created successfully!`);
        
        // Reset form
        setFormData({
          guestName: '',
          guestEmail: '',
          guestPhone: '',
          checkIn: '',
          checkOut: '',
          numGuests: 1,
          numRooms: 1,
          selectedRoomId: '',
          paymentMethod: 'cash',
          specialRequests: ''
        });
        
        onOpenChange(false);
        if (onBookingCreated) onBookingCreated();
      } else {
        toast.error(data.message || 'Failed to create booking');
      }
    } catch (error) {
      console.error('Booking creation error:', error);
      toast.error('Failed to create booking');
    } finally {
      setLoading(false);
    }
  };

  const nights = calculateNights();
  const estimatedPrice = calculateEstimatedPrice();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="booking-creation-dialog">
        {/* Modern Gradient Header */}
        <div className="bg-gradient-to-br from-blue-600 via-cyan-600 to-teal-600 text-white px-6 py-5">
          <div className="flex items-center gap-3">
            <div className="bg-white/20 p-3 rounded-xl">
              <Calendar className="w-6 h-6 text-white" />
            </div>
            <div>
              <DialogTitle className="text-2xl font-bold text-white mb-1">
                Create New Booking
              </DialogTitle>
              <p className="text-white/90 text-sm">Reserve a room for your guest</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Guest Information Card */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border-2 border-blue-100 rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-600" />
              Guest Information
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="guest-name" className="text-sm font-semibold text-gray-700">
                  Full Name *
                </Label>
                <Input
                  id="guest-name"
                  value={formData.guestName}
                  onChange={(e) => setFormData({...formData, guestName: e.target.value})}
                  placeholder="John Doe"
                  className="h-11"
                  data-testid="guest-name-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="guest-email" className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <Mail className="w-4 h-4" />
                  Email Address *
                </Label>
                <Input
                  id="guest-email"
                  type="email"
                  value={formData.guestEmail}
                  onChange={(e) => setFormData({...formData, guestEmail: e.target.value})}
                  placeholder="john@example.com"
                  className="h-11"
                  data-testid="guest-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="guest-phone" className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                <Phone className="w-4 h-4" />
                Phone Number (Optional)
              </Label>
              <Input
                id="guest-phone"
                type="tel"
                value={formData.guestPhone}
                onChange={(e) => setFormData({...formData, guestPhone: e.target.value})}
                placeholder="+1 (555) 000-0000"
                className="h-11"
                data-testid="guest-phone-input"
              />
            </div>
          </div>

          <Separator />

          {/* Booking Details Card */}
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 border-2 border-purple-100 rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-purple-600" />
              Stay Details
            </h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="check-in" className="text-sm font-semibold text-gray-700">
                  Check-In Date *
                </Label>
                <Input
                  id="check-in"
                  type="date"
                  value={formData.checkIn}
                  onChange={(e) => setFormData({...formData, checkIn: e.target.value})}
                  className="h-11"
                  data-testid="check-in-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="check-out" className="text-sm font-semibold text-gray-700">
                  Check-Out Date *
                </Label>
                <Input
                  id="check-out"
                  type="date"
                  value={formData.checkOut}
                  onChange={(e) => setFormData({...formData, checkOut: e.target.value})}
                  min={formData.checkIn}
                  className="h-11"
                  data-testid="check-out-input"
                />
              </div>
            </div>

            {nights > 0 && (
              <div className="bg-purple-100 border border-purple-300 rounded-lg p-3 text-center">
                <span className="font-bold text-purple-900 text-lg">{nights} Night{nights !== 1 ? 's' : ''}</span>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="num-guests" className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <Users className="w-4 h-4" />
                  Number of Guests
                </Label>
                <Input
                  id="num-guests"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.numGuests}
                  onChange={(e) => setFormData({...formData, numGuests: e.target.value})}
                  className="h-11"
                  data-testid="num-guests-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="num-rooms" className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <Bed className="w-4 h-4" />
                  Number of Rooms
                </Label>
                <Input
                  id="num-rooms"
                  type="number"
                  min="1"
                  max="5"
                  value={formData.numRooms}
                  onChange={(e) => setFormData({...formData, numRooms: e.target.value})}
                  className="h-11"
                  data-testid="num-rooms-input"
                />
              </div>
            </div>
          </div>

          <Separator />

          {/* Room Selection Card */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-100 rounded-xl p-5 space-y-4">
            <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
              <Bed className="w-5 h-5 text-green-600" />
              Room Selection
            </h3>
            
            {loadingRooms ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-green-600 mr-2" />
                <span className="text-sm text-gray-600">Loading available rooms...</span>
              </div>
            ) : availableRooms.length === 0 ? (
              <Alert className="bg-red-50 border-red-200">
                <AlertDescription className="text-red-800">
                  No rooms available. Please add rooms in the Room Manager.
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="room-select" className="text-sm font-semibold text-gray-700">
                  Select Room Type *
                </Label>
                <Select 
                  value={formData.selectedRoomId} 
                  onValueChange={(value) => setFormData({...formData, selectedRoomId: value})}
                >
                  <SelectTrigger className="h-11" data-testid="room-select">
                    <SelectValue placeholder="Choose a room type" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableRooms.map((room) => (
                      <SelectItem key={room.id} value={room.id}>
                        <div className="flex items-center justify-between w-full gap-6">
                          <span className="font-semibold">{room.name || room.room_type_name}</span>
                          <span className="text-gray-600">${room.price_per_night || 0}/night</span>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.selectedRoomId && (
              <div className="bg-green-100 border border-green-300 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-green-900">
                      {availableRooms.find(r => r.id === formData.selectedRoomId)?.name || 
                       availableRooms.find(r => r.id === formData.selectedRoomId)?.room_type_name}
                    </p>
                    <p className="text-sm text-green-700">
                      Max {availableRooms.find(r => r.id === formData.selectedRoomId)?.max_guests || 2} guests
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-green-900">
                      ${availableRooms.find(r => r.id === formData.selectedRoomId)?.price_per_night || 0}
                    </p>
                    <p className="text-xs text-green-700">per night</p>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Payment & Special Requests */}
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="payment-method" className="text-sm font-semibold text-gray-700 flex items-center gap-1">
                  <DollarSign className="w-4 h-4" />
                  Payment Method
                </Label>
                <Select 
                  value={formData.paymentMethod} 
                  onValueChange={(value) => setFormData({...formData, paymentMethod: value})}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="card">Credit/Debit Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="crypto">Cryptocurrency</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {estimatedPrice > 0 && (
                <div className="bg-gradient-to-br from-blue-500 to-cyan-500 text-white rounded-xl p-4 flex flex-col justify-center">
                  <p className="text-sm opacity-90">Estimated Total</p>
                  <p className="text-3xl font-bold">${estimatedPrice}</p>
                  <p className="text-xs opacity-75">{nights} nights × {formData.numRooms} room(s)</p>
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="special-requests" className="text-sm font-semibold text-gray-700">
                Special Requests (Optional)
              </Label>
              <textarea
                id="special-requests"
                value={formData.specialRequests}
                onChange={(e) => setFormData({...formData, specialRequests: e.target.value})}
                placeholder="Any special requirements or requests..."
                className="w-full min-h-[80px] px-3 py-2 text-sm border rounded-md resize-none"
                data-testid="special-requests-input"
              />
            </div>
          </div>
        </form>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <Button 
            type="button"
            variant="outline" 
            onClick={() => onOpenChange(false)}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading || !formData.guestName || !formData.guestEmail || !formData.checkIn || !formData.checkOut || !formData.selectedRoomId}
            className="px-8 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold shadow-lg"
            data-testid="create-booking-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Create Booking
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
